import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { reverse } from "named-urls";
import { Table, Space, PageHeader } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { userListingSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import { useLocation, useHistory } from "react-router-dom";

const { Column } = Table;
const STORAGE_KEY = 'addressSummaryFilters';

const AddressSummaryListing = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: userListingSaga });
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    return { page: 1, companyName: "" };
  };

  const initialState = getInitialState();
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [companyNameFilter, setCompanyNameFilter] = useState(initialState.companyName);

  const summaryData = useSelector(selectors.selectSummaryData) || [];
  const totalCompanyCount = useSelector(selectors.selectCompanyCount);
  const totalCompanies = useSelector(selectors.selectTotalCompanies);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = { page: currentPage, companyName: companyNameFilter };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [currentPage, companyNameFilter]);

  // Fetch data when filters change
  useEffect(() => {
    const query = `page=${currentPage}&limit=10${
      companyNameFilter ? `&companyName=${companyNameFilter}` : ""
    }`;
    dispatch(actions.fetchAddressSummary({ query }));
  }, [dispatch, currentPage, companyNameFilter]);

  // Fetch total companies on mount
  useEffect(() => {
    dispatch(actions.fetchTotalCompanies());
  }, [dispatch]);

  const companyData = summaryData.map((company) => ({
    key: company._id,
    id: company._id,
    company: company.companyName,
  }));

  const getCompanyFilters = () => {
    if (!totalCompanies) return [];
    const sortedCompanies = [...totalCompanies].sort((a, b) =>
      a.companyName.localeCompare(b.companyName)
    );
    return sortedCompanies.map((entity) => ({
      text: entity.companyName,
      value: entity.companyName,
    }));
  };

  const handleTablePagination = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current || 1);
    setCompanyNameFilter(filters.company ? filters.company[0] : "");
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader ghost={false} title="Addresses Management" />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table
          dataSource={companyData}
          onChange={handleTablePagination}
          pagination={{
            current: currentPage,
            pageSize: 10,
            total: totalCompanyCount,
            showSizeChanger: false,
            disabled: companyData.length === 0,
          }}
        >
          <Column
            title="Company Name"
            dataIndex="company"
            key="company"
            filters={getCompanyFilters()}
            filterSearch={true}
            filteredValue={companyNameFilter ? [companyNameFilter] : null}
            width="30%"
          />
          <Column
            title="Action"
            key="action"
            width="25%"
            render={(text, record) => (
              <Space size="middle">
                <Link
                  to={reverse(
                    routes.backOffice.companies.details.billingAddresses.list,
                    {
                      companyId: record.id,
                    }
                  )}
                >
                  View Billing Addresses
                </Link>
                <Link
                  to={reverse(
                    routes.backOffice.companies.details.pickupAddresses.list,
                    {
                      companyId: record.id,
                    }
                  )}
                >
                  View Pickup Addresses
                </Link>
              </Space>
            )}
          />
        </Table>
      </div>
    </div>
  );
};

export default AddressSummaryListing;